import { useMemo } from 'react';

import {
  hasPermission,
  canAccessRoute,
  hasSpecialRole,
  hasAnyPermission,
  hasAllPermissions,
  isValidPermission,
  getPermissionDescription,
  groupPermissionsByCategory,
  filterNavItemsByPermissions,
} from 'src/utils/permissions';

import { useAuthContext } from 'src/auth/hooks';

// ----------------------------------------------------------------------

/**
 * Hook personalizado para manejo de permisos
 * Proporciona todas las funcionalidades necesarias para verificar permisos de usuario
 * de manera eficiente y escalable
 */
export const usePermissions = () => {
  const { user } = useAuthContext();

  // Memoizar las funciones de verificación de permisos para optimizar rendimiento
  const permissions = useMemo(() => {
    if (!user) {
      // Si no hay usuario, retornar funciones que siempre devuelven false
      return {
        hasPermission: () => false,
        hasAnyPermission: () => false,
        hasAllPermissions: () => false,
        canAccessRoute: () => false,
        hasSpecialRole: () => false,
        filterNavItems: () => [],
        getPermissionDescription: (permission) => permission,
        groupPermissionsByCategory: () => ({}),
        isValidPermission: () => false,
        userPermissions: [],
        userRoles: [],
        isAuthenticated: false,
      };
    }

    return {
      /**
       * Verifica si el usuario tiene un permiso específico
       * @param {string} permission - Permiso a verificar
       * @returns {boolean}
       */
      hasPermission: (permission) => hasPermission(user, permission),

      /**
       * Verifica si el usuario tiene alguno de los permisos especificados
       * @param {string|string[]} perms - Permiso(s) a verificar
       * @returns {boolean}
       */
      hasAnyPermission: (perms) => hasAnyPermission(user, perms),

      /**
       * Verifica si el usuario tiene todos los permisos especificados
       * @param {string|string[]} perms - Permiso(s) a verificar
       * @returns {boolean}
       */
      hasAllPermissions: (perms) => hasAllPermissions(user, perms),

      /**
       * Verifica si el usuario puede acceder a una ruta específica
       * @param {string} route - Ruta a verificar
       * @returns {boolean}
       */
      canAccessRoute: (route) => canAccessRoute(user, route),

      /**
       * Verifica si el usuario tiene un rol especial (master, admin)
       * @returns {boolean}
       */
      hasSpecialRole: () => hasSpecialRole(user),

      /**
       * Filtra elementos de navegación basado en permisos del usuario
       * @param {Array} navItems - Array de elementos de navegación
       * @returns {Array} - Array filtrado de elementos de navegación
       */
      filterNavItems: (navItems) => filterNavItemsByPermissions(user, navItems),

      /**
       * Obtiene la descripción legible de un permiso
       * @param {string} permission - Elemento de permiso
       * @returns {string} - Descripción legible
       */
      getPermissionDescription: (permission) => getPermissionDescription(permission),

      /**
       * Agrupa permisos por categoría
       * @param {string[]} perms - Array de permisos (opcional, usa permisos del usuario por defecto)
       * @returns {Object} - Objeto agrupado por categoría
       */
      groupPermissionsByCategory: (perms = user.permissions || []) =>
        groupPermissionsByCategory(perms),

      /**
       * Valida formato de permiso
       * @param {string} permission - Permiso a validar
       * @returns {boolean}
       */
      isValidPermission: (permission) => isValidPermission(permission),

      // Propiedades del usuario para acceso directo
      userPermissions: user.permissions || [],
      userRoles: user.roles || [],
      isAuthenticated: true,
    };
  }, [user]);

  return permissions;
};

/**
 * Hook específico para verificar un permiso simple
 * Útil para casos donde solo necesitas verificar un permiso específico
 * @param {string} permission - Permiso a verificar
 * @returns {boolean}
 */
export const useHasPermission = (permission) => {
  const { hasPermission: checkPermission } = usePermissions();

  return useMemo(() => {
    if (!permission) return false;
    return checkPermission(permission);
  }, [checkPermission, permission]);
};

/**
 * Hook específico para verificar múltiples permisos (OR logic)
 * @param {string|string[]} permissions - Permiso(s) a verificar
 * @returns {boolean}
 */
export const useHasAnyPermission = (permissions) => {
  const { hasAnyPermission: checkAnyPermission } = usePermissions();

  return useMemo(() => {
    if (!permissions) return false;
    return checkAnyPermission(permissions);
  }, [checkAnyPermission, permissions]);
};

/**
 * Hook específico para verificar múltiples permisos (AND logic)
 * @param {string|string[]} permissions - Permiso(s) a verificar
 * @returns {boolean}
 */
export const useHasAllPermissions = (permissions) => {
  const { hasAllPermissions: checkAllPermissions } = usePermissions();

  return useMemo(() => {
    if (!permissions) return false;
    return checkAllPermissions(permissions);
  }, [checkAllPermissions, permissions]);
};

/**
 * Hook específico para verificar acceso a rutas
 * @param {string} route - Ruta a verificar
 * @returns {boolean}
 */
export const useCanAccessRoute = (route) => {
  const { canAccessRoute: checkRouteAccess } = usePermissions();

  return useMemo(() => {
    if (!route) return false;
    return checkRouteAccess(route);
  }, [checkRouteAccess, route]);
};

/**
 * Hook para obtener permisos agrupados por categoría
 * Útil para mostrar permisos en interfaces de administración
 * @returns {Object} - Permisos agrupados por categoría
 */
export const useGroupedPermissions = () => {
  const { groupPermissionsByCategory: groupPerms, userPermissions } = usePermissions();

  return useMemo(() => groupPerms(userPermissions), [groupPerms, userPermissions]);
};

/**
 * Hook para filtrar elementos de navegación
 * @param {Array} navItems - Array de elementos de navegación
 * @returns {Array} - Array filtrado de elementos de navegación
 */
export const useFilteredNavigation = (navItems) => {
  const { filterNavItems } = usePermissions();

  return useMemo(() => {
    if (!navItems || !Array.isArray(navItems)) return [];
    return filterNavItems(navItems);
  }, [filterNavItems, navItems]);
};

/**
 * Hook para verificar si el usuario es administrador o master
 * @returns {boolean}
 */
export const useIsAdmin = () => {
  const { hasSpecialRole: checkSpecialRole } = usePermissions();

  return useMemo(() => checkSpecialRole(), [checkSpecialRole]);
};
